import { useEffect, useMemo, useState } from "react";
import { Alert, Button, Form, Modal } from "react-bootstrap";
import { Link } from "react-router-dom";
import UserService from "../services/data.service";
import ErrorBoundary from "./ErrorBoundary";
import RegisterService from "../services/register.service";

const Users = () => {
  const [users, setUsers] = useState([]);
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetUsername, setResetUsername] = useState("");
  const [resetUserId, setResetUserId] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [resetMessage, setResetMessage] = useState("");
  const [resetSuccessMessage, setResetSuccessMessage] = useState("");
  const [updatePhoneMessage, setUpdatePhoneMessage] = useState("");
  const [updatePhoneSuccessful, setUpdatePhoneSuccessful] = useState("");
  const [editUserId, setEditUserId] = useState("");
  const [editUserPhonenumber, setEditUserPhonenumber] = useState("");
  const [showEditUser, setShowEditUser] = useState(false);

  const loadUsers = () => {
    UserService.getUsers().then((response) => {
      setUsers(response.data);
    });
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const metrics = useMemo(() => {
    const adminCount = users.filter((user) => user.userRoles.includes("Admin") || user.userRoles.includes("ADMIN")).length;

    return {
      totalUsers: users.length,
      adminCount,
      operatorCount: users.length - adminCount,
    };
  }, [users]);

  const handleCloseReset = () => {
    setPassword("");
    setConfirmPassword("");
    setResetMessage("");
    setResetSuccessMessage("");
    setShowResetModal(false);
  };

  const handleCloseEdit = () => {
    setResetUsername("");
    setEditUserId("");
    setEditUserPhonenumber("");
    setShowEditUser(false);
    setUpdatePhoneMessage("");
    setUpdatePhoneSuccessful("");
    loadUsers();
  };

  const handleSubmitReset = () => {
    if (password !== confirmPassword) {
      setResetMessage("Passwords don't match.");
      setResetSuccessMessage("");
      return;
    }

    if (password === "" || confirmPassword === "") {
      setResetMessage("Password cannot be empty.");
      setResetSuccessMessage("");
      return;
    }

    RegisterService.resetPassword(resetUserId, password).then(
      (response) => {
        if (response.data.succeeded) {
          setPassword("");
          setConfirmPassword("");
          setResetSuccessMessage("Password reset successful.");
          setResetMessage("");
        } else {
          setResetMessage(response.data.errors[0].description);
          setResetSuccessMessage("");
        }
      },
      (error) => {
        const responseMessage =
          (error.response && error.response.data && error.response.data.message) ||
          error.message ||
          error.toString();
        setResetMessage(responseMessage);
        setResetSuccessMessage("");
      }
    );
  };

  const handleSubmitEdit = () => {
    RegisterService.updatePhoneNumber(editUserId, editUserPhonenumber).then(
      () => {
        setUpdatePhoneSuccessful("Phone number updated successfully.");
        setUpdatePhoneMessage("");
        loadUsers();
      },
      () => {
        setUpdatePhoneMessage("Phone update failed.");
        setUpdatePhoneSuccessful("");
      }
    );
  };

  return (
    <div className="portal-page-stack">
      <section className="metrics-grid">
        <article className="metric-card">
          <p className="metric-card__label">Portal users</p>
          <h4>{metrics.totalUsers}</h4>
          <span>All portal accounts with access to administrative or payment workflows.</span>
        </article>
        <article className="metric-card metric-card--highlight">
          <p className="metric-card__label">Administrators</p>
          <h4>{metrics.adminCount}</h4>
          <span>Accounts with elevated access to clients, users, and cross-client reporting.</span>
        </article>
        <article className="metric-card">
          <p className="metric-card__label">Operators</p>
          <h4>{metrics.operatorCount}</h4>
          <span>Users focused on day-to-day transaction processing and operational reporting.</span>
        </article>
      </section>

      <section className="content-card content-card--padded">
        <div className="section-heading">
          <div>
            <p className="section-heading__eyebrow">Access control</p>
            <h4>Portal users and support actions</h4>
          </div>
          <div className="button-row">
            <Button variant="outline-primary" onClick={loadUsers}>
              Refresh
            </Button>
            <Link to="/createuser">
              <Button variant="primary">Add user</Button>
            </Link>
          </div>
        </div>
        {users.length ? (
          <div className="table-responsive portal-table-wrap">
            <table className="table portal-table align-middle mb-0">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Username</th>
                  <th>Email</th>
                  <th>Roles</th>
                  <th>Phone number</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.users.id}>
                    <td>{user.users.id}</td>
                    <td>{user.users.userName}</td>
                    <td>{user.users.email}</td>
                    <td>{user.userRoles.join(", ")}</td>
                    <td>{user.users.phoneNumber || "—"}</td>
                    <td>
                      <div className="table-action-row">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => {
                            setShowResetModal(true);
                            setResetUsername(user.users.userName);
                            setResetUserId(user.users.id);
                            setResetMessage("");
                            setResetSuccessMessage("");
                          }}
                        >
                          Reset password
                        </Button>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => {
                            setShowEditUser(true);
                            setResetUsername(user.users.userName);
                            setEditUserId(user.users.id);
                            setEditUserPhonenumber(user.users.phoneNumber || "");
                            setUpdatePhoneMessage("");
                            setUpdatePhoneSuccessful("");
                          }}
                        >
                          Update phone
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="empty-state empty-state--compact">
            <span className="bi bi-people" aria-hidden="true" />
            <h5>No users available</h5>
            <p>Create the first operator or administrator to activate the portal workspace.</p>
          </div>
        )}
      </section>

      <Modal show={showResetModal} onHide={handleCloseReset} centered>
        <Modal.Header closeButton>
          <Modal.Title>Reset password</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="modal-supporting-text">
            Use this carefully. Resetting credentials immediately affects the selected user's access.
          </p>
          <Form>
            <Form.Group className="mb-3" controlId="resetPasswordUsername">
              <Form.Label>User name</Form.Label>
              <Form.Control type="text" disabled value={resetUsername} />
            </Form.Group>
            <Form.Group className="mb-3" controlId="resetPasswordValue">
              <Form.Label>Password</Form.Label>
              <Form.Control type="password" value={password} onChange={(event) => setPassword(event.target.value)} />
            </Form.Group>
            <Form.Group className="mb-0" controlId="resetPasswordConfirmValue">
              <Form.Label>Confirm password</Form.Label>
              <Form.Control
                type="password"
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
              />
            </Form.Group>
          </Form>
          {resetMessage ? <Alert variant="danger" className="mt-3 mb-0">{resetMessage}</Alert> : null}
          {resetSuccessMessage ? <Alert variant="success" className="mt-3 mb-0">{resetSuccessMessage}</Alert> : null}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={handleCloseReset}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmitReset}>
            Reset password
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showEditUser} onHide={handleCloseEdit} centered>
        <Modal.Header closeButton>
          <Modal.Title>Update phone number</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3" controlId="updateUserPhoneUsername">
              <Form.Label>User name</Form.Label>
              <Form.Control type="text" disabled value={resetUsername} />
            </Form.Group>
            <Form.Group className="mb-0" controlId="updateUserPhoneNumber">
              <Form.Label>Phone number</Form.Label>
              <Form.Control
                type="text"
                value={editUserPhonenumber}
                onChange={(event) => setEditUserPhonenumber(event.target.value)}
              />
            </Form.Group>
          </Form>
          {updatePhoneMessage ? <Alert variant="danger" className="mt-3 mb-0">{updatePhoneMessage}</Alert> : null}
          {updatePhoneSuccessful ? <Alert variant="success" className="mt-3 mb-0">{updatePhoneSuccessful}</Alert> : null}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={handleCloseEdit}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmitEdit}>
            Update phone
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

const Wrapper = () => {
  return (
    <ErrorBoundary>
      <Users />
    </ErrorBoundary>
  );
};

export default Wrapper;
